import Chance from '../utils/Chance'
import Card from './Card'
import Gold from './cards/Gold'
import Monster, { MonsterAbility } from './cards/Monster'

interface IMonsterConfig {
  name: string
  emoji: string
  valueRange: [number, number]
  abilities?: MonsterAbility[]
  isGuard?: boolean
  appearsStartingFloor?: number
}

export default class MonsterFactory {
  private static monsters: IMonsterConfig[] = [
    {
      name: 'Imp',
      emoji: '👿',
      valueRange: [10, 20],
      abilities: [MonsterAbility.Greedy]
    },
    {
      name: 'Ogre',
      emoji: '👹',
      valueRange: [3, 7]
    },
    {
      name: 'Bug',
      emoji: '🐛',
      valueRange: [1, 2],
      abilities: [MonsterAbility.Constant, MonsterAbility.Infector]
    },
    {
      name: 'Ant',
      emoji: '🐜',
      valueRange: [1, 3],
      abilities: [MonsterAbility.Constant, MonsterAbility.Infector, MonsterAbility.Multiplier]
    },
    {
      name: 'Octopus',
      emoji: '🐙',
      valueRange: [3, 8],
    },
    {
      name: 'Squid',
      emoji: '🦑',
      valueRange: [4, 10],
    },
    {
      name: 'Goblin',
      emoji: '👺',
      valueRange: [2, 5],
      appearsStartingFloor: 6,
      abilities: [MonsterAbility.GoldDigger]
    },
    {
      name: 'Clown',
      emoji: '🤡',
      valueRange: [1, 2],
      appearsStartingFloor: 5,
      abilities: [MonsterAbility.Copycat, MonsterAbility.Constant]
    },
    {
      name: 'Ghost',
      emoji: '👻',
      valueRange: [5, 9],
      abilities: [MonsterAbility.Persistant],
      appearsStartingFloor: 3
    },
    {
      name: 'Skull',
      emoji: '💀',
      valueRange: [17, 26],
      appearsStartingFloor: 7
    },
    {
      name: 'Alien',
      emoji: '👾',
      valueRange: [15, 20],
      appearsStartingFloor: 6,
    },
    {
      name: 'Robot',
      emoji: '🤖',
      valueRange: [20, 24],
      appearsStartingFloor: 10
    },
    {
      name: 'Pumpkin',
      emoji: '🎃',
      valueRange: [10, 24],
      abilities: [MonsterAbility.Persistant],
      appearsStartingFloor: 9
    },
    {
      name: 'Spider',
      emoji: '🕷',
      valueRange: [3, 7],
      appearsStartingFloor: 11,
      abilities: [MonsterAbility.Constant, MonsterAbility.Infector, MonsterAbility.Multiplier]
    },
    {
      name: 'Snail',
      emoji: '🐌',
      valueRange: [2, 4],
      appearsStartingFloor: 9,
      abilities: [MonsterAbility.Constant]
    },
    {
      name: 'Wolf',
      emoji: '🐺',
      valueRange: [10, 24],
      appearsStartingFloor: 9
    },
    {
      name: 'Bee',
      emoji: '🐝',
      valueRange: [2, 5],
      appearsStartingFloor: 12,
      abilities: [MonsterAbility.Constant, MonsterAbility.Multiplier]
    },
    {
      name: 'Scorpion',
      emoji: '🦂',
      valueRange: [10, 24],
      appearsStartingFloor: 10,
      abilities: [MonsterAbility.Infector]
    },
    {
      name: 'Snake',
      emoji: '🐍',
      valueRange: [10, 24],
      appearsStartingFloor: 10,
    },
    {
      name: 'Lizard',
      emoji: '🦎',
      valueRange: [10, 24],
      appearsStartingFloor: 10,
    },
    {
      name: 'TRex',
      emoji: '🦖',
      valueRange: [20, 30],
      appearsStartingFloor: 16,
      abilities: [MonsterAbility.Shy]
    }
  ]

  static getEmojiFor(name: string) {
    return this.monsters.find(m => m.name === name)?.emoji
  }

  static getMonstersForLevel(level: number, limit = 999) {
    return this.monsters
      .filter(monster => level >= (monster.appearsStartingFloor || 1))
      .sort((a, b) => (b.appearsStartingFloor || 1) - (a.appearsStartingFloor || 1))
      .slice(0, limit)
  }

  static createForLevel(level: number) {
    const monster = Chance.in(...this.getMonstersForLevel(level, 5))

    let n = Chance.range(monster.valueRange[0], monster.valueRange[1])
    n = Chance.weighted([n, 0], [60, 40])

    const value = Chance.range(monster.valueRange[0], monster.valueRange[1])

    n = Chance.weighted([n, 0], [60, 40])
    const loot = n ? new Gold(n) : undefined

    return new Monster(monster.name, value, loot, monster.abilities)
  }
}
